import type {
  OriginNewProductBundlePayload,
  OriginNewProductCartPayload,
  OriginNewProductPayload,
  OriginNewProductSinglePayload,
  OriginSpec,
  OriginSubmitPayload,
} from '@/types/origin'

export type SignBuilderCartProductEntry = {
  spec: OriginSpec
  qty?: number
}

type NewProductWirePayload = Extract<OriginSubmitPayload, { tile: 'new_product' }>

function readNewProductVariant(
  payload: NewProductWirePayload
): OriginNewProductPayload['new_product_variant'] | undefined {
  if ('new_product_variant' in payload) {
    const variant = payload.new_product_variant
    if (variant === 'single' || variant === 'cart') {
      return variant
    }
  }
  return undefined
}

/** Type-safe narrowing for single-product new_product payloads (includes legacy rows without variant). */
export function isOriginNewProductSinglePayload(
  payload: OriginSubmitPayload
): payload is OriginNewProductSinglePayload {
  if (payload.tile !== 'new_product') {
    return false
  }
  const variant = readNewProductVariant(payload)
  if (variant === 'single') {
    return true
  }
  if (variant === 'cart') {
    return false
  }
  return 'spec' in payload && !('products' in payload)
}

/** Type-safe narrowing for multi-product cart new_product payloads (includes legacy rows without variant). */
export function isOriginNewProductCartPayload(
  payload: OriginSubmitPayload
): payload is OriginNewProductCartPayload {
  if (payload.tile !== 'new_product') {
    return false
  }
  const variant = readNewProductVariant(payload)
  if (variant === 'cart') {
    return true
  }
  if (variant === 'single') {
    return false
  }
  return 'products' in payload && Array.isArray(payload.products)
}

export function isSellableBundlePayload(payload: OriginSubmitPayload): boolean {
  return payload.tile === 'new_product_bundle' && payload.is_bundle === true
}

/** Sign Builder cart (new wire or legacy PR1 rows without is_bundle), including single-item products[] rows. */
export function isSignBuilderCartPayload(payload: OriginSubmitPayload): boolean {
  if (isOriginNewProductCartPayload(payload)) {
    return payload.products.length >= 1
  }
  if (payload.tile === 'new_product_bundle') {
    return (payload as OriginNewProductBundlePayload).is_bundle !== true
  }
  return false
}

export function getSignBuilderCartProducts(
  payload: OriginSubmitPayload
): SignBuilderCartProductEntry[] {
  if (!isSignBuilderCartPayload(payload)) {
    return []
  }
  if (isOriginNewProductCartPayload(payload)) {
    return payload.products.map((product) => ({
      spec: product.spec,
    }))
  }
  if (payload.tile === 'new_product_bundle') {
    return payload.products.map((product) => ({
      spec: product.spec,
      qty: product.qty,
    }))
  }
  return []
}

export function summarizeSignBuilderCartProducts(products: SignBuilderCartProductEntry[]): string {
  if (products.length === 0) return '(unnamed)'
  const firstDesc = products[0]?.spec?.product_description?.trim() || '(unnamed)'
  if (products.length === 1) {
    const cat = products[0]?.spec?.category?.label
    return cat ? `${firstDesc} — ${cat}` : firstDesc
  }
  const more = products.length - 1
  return `${firstDesc} (+${more} more)`
}
